import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';
import ReviewList from './ReviewList';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const productData = await response.json();
        setProduct(productData);
      } else {
        throw new Error('Product not found');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast({
        title: "Error",
        description: "Failed to load product details.",
        variant: "destructive",
      });
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: product._id,
          quantity: quantity
        }),
      });

      if (response.ok) {
        // Update local cart for immediate UI feedback
        const cart = JSON.parse(localStorage.getItem('dcTech_cart') || '[]');
        const existingItem = cart.find(item => item._id === product._id);
        
        if (existingItem) {
          existingItem.quantity += quantity;
        } else {
          cart.push({ ...product, quantity: quantity });
        }
        
        localStorage.setItem('dcTech_cart', JSON.stringify(cart));
        
        toast({
          title: "Added to cart",
          description: `${quantity}x ${product.name} has been added to your cart.`,
        });
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading product details...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Product not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
                <span className="text-[10px] text-white/70">ECRA/ESA #7018534</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Dashboard
                </Button>
              </Link>
              
              <Link to="/cart">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Cart
                </Button>
              </Link>
              
              <Button 
                onClick={logout}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Product Images */}
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-6">
              <img 
                src={product.image || '/api/placeholder/500/500'} 
                alt={product.name}
                className="w-full h-96 object-cover rounded-lg mb-4"
              />
            </CardContent>
          </Card>

          {/* Product Information */}
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-white text-3xl mb-2">{product.name}</CardTitle>
                  <Badge className="bg-blue-500 text-white">{product.category}</Badge>
                </div>
                <div className="text-right">
                  {product.stock > 0 ? (
                    <Badge className="bg-green-500 text-white">In Stock ({product.stock})</Badge>
                  ) : (
                    <Badge className="bg-red-500 text-white">Out of Stock</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Rating */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-yellow-400 text-xl">⭐ {product.rating}</span>
                  <span className="text-white/60">({product.reviews} reviews)</span>
                </div>
              </div>

              {/* Price */}
              <div className="flex items-center gap-4">
                <span className="text-4xl font-bold text-green-400">${product.price}</span>
                {product.originalPrice && (
                  <>
                    <span className="text-2xl text-white/60 line-through">${product.originalPrice}</span>
                    <Badge className="bg-red-500 text-white text-lg">{product.discount}% OFF</Badge>
                  </>
                )}
              </div>

              {/* Description */}
              <p className="text-white/80 text-lg leading-relaxed">{product.description}</p>

              {/* Shipping */}
              {product.shipping && (
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-green-400">🚚</span>
                    <span className="text-white">{product.shipping}</span>
                  </div>
                </div>
              )}

              {/* Quantity and Add to Cart */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-white font-semibold">Quantity:</label>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-white/50 text-white bg-white/10"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      -
                    </Button>
                    <span className="text-white font-semibold px-4 py-2 bg-white/10 rounded">{quantity}</span>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-white/50 text-white bg-white/10"
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      disabled={quantity >= product.stock}
                    >
                      +
                    </Button>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button 
                    onClick={addToCart}
                    disabled={product.stock === 0}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-teal-500   text-white text-lg py-6"
                  >
                    Add to Cart - ${(product.price * quantity).toFixed(2)}
                  </Button>
                  <Link to="/cart" className="flex-1">
                    <Button 
                      variant="outline" 
                      className="w-full border-white/50 text-white bg-white/10 text-lg py-6"
                    >
                      View Cart
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Product Details Tabs */}
        <div className="mt-8 space-y-6">
          {/* Specifications */}
          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Specifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white/70 font-medium">{key}:</span>
                      <span className="text-white">{value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Features */}
          {product.features && product.features.length > 0 && (
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Features</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="text-green-400 text-xl">✓</div>
                      <span className="text-white/80 text-lg">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Reviews Section */}
      <div className="mt-12">
        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Reviews</h2>
          <ReviewList 
            productId={id}
            maxReviews={5}
            showProviderResponse={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;